import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { applyButtonTheme, getButtonClasses, getThemeShapeClasses } from "../utils/themeUtils"
import { buildServiceUrl, buildBookingUrl, buildPublicPageUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const AboutSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Generate missing colors if they don't exist
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    // If bgMedium, bgExtraLight, or bgLight are missing, generate them from primary color
    if (!colors.bgMedium || !colors.bgExtraLight || !colors.bgLight) {
      const primaryColor = colors.primary
      if (primaryColor) {
        const generatedPalette = generateColorPalette(primaryColor)
        colors.bgMedium = colors.bgMedium || generatedPalette.bgMedium
        colors.bgExtraLight = colors.bgExtraLight || generatedPalette.bgExtraLight
        colors.bgLight = colors.bgLight || generatedPalette.bgLight
      }
    }

    return colors
  }

  const themeColors = getThemeColors()
  const layout = settings.layout || "image_left"
  const showCTA = settings.showCTA !== undefined ? settings.showCTA : true
  const ctaText = settings.ctaText || "Learn more"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || ""

  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled
    if (settings.showBackgroundColor && settings.backgroundColor === "theme") {
      // Use alert yellow if bgLight is missing to surface misconfiguration
      return themeColors.bgLight || "#FFF200"
    }
    // Default to white if toggle is off or not set
    return "#FFFFFF"
  }

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.open(buildServiceUrl(ctaTarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  const renderContent = () => (
    <div className="flex w-full max-w-full flex-col gap-6">
      {/* Text Content */}
      <div className="flex w-full flex-col gap-6">
        {section.headline && (
          <h2
            className="w-full text-[24px] font-bold leading-[28px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: theme?.colors?.textDark || "#000000"
            }}>
            {section.headline}
          </h2>
        )}
        {section.body && (
          <>
            <style>{`
              .about-section-content {
                text-align: initial !important;
              }
              .about-section-content ul {
                list-style-type: disc !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .about-section-content ol {
                list-style-type: decimal !important;
                margin-left: 1.5rem !important;
                margin-bottom: 1rem !important;
              }
              .about-section-content li {
                margin-bottom: 0.5rem !important;
                display: list-item !important;
              }
              .about-section-content .ql-align-right {
                text-align: right !important;
              }
              .about-section-content .ql-align-center {
                text-align: center !important;
              }
              .about-section-content .ql-align-justify {
                text-align: justify !important;
              }
              .about-section-content .ql-align-left {
                text-align: left !important;
              }
              .about-section-content a {
                color: ${theme?.colors?.primary || "#BD654F"} !important;
                text-decoration: underline !important;
              }
              .about-section-content a:hover {
                color: ${theme?.colors?.primaryDark || "#A55A45"} !important;
                text-decoration: underline !important;
              }
              .about-section-content p {
                margin-bottom: 1rem !important;
                margin-top: 0 !important;
              }
              .about-section-content p:first-child {
                margin-top: 0 !important;
              }
              .about-section-content p:last-child {
                margin-bottom: 0 !important;
              }
              .about-section-content strong,
              .about-section-content b {
                font-weight: ${theme?.fonts?.paragraphBoldWeight || "600"} !important;
              }
              .about-section-content em,
              .about-section-content i {
                font-style: italic !important;
              }
              .about-section-content u {
                text-decoration: underline !important;
              }
              .about-section-content h1 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 32px !important;
                line-height: 48px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${theme?.colors?.textDark || "#000000"} !important;
              }
              .about-section-content h2 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 27px !important;
                line-height: 32px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${theme?.colors?.textDark || "#000000"} !important;
              }
              .about-section-content h3 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 22px !important;
                line-height: 32px !important;
                margin-bottom: 1rem !important;
                margin-top: 1.5rem !important;
                color: ${theme?.colors?.textDark || "#000000"} !important;
              }
              .about-section-content h4 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 18px !important;
                line-height: 24px !important;
                margin-bottom: 0.75rem !important;
                margin-top: 1.25rem !important;
                color: ${theme?.colors?.textDark || "#000000"} !important;
              }
              .about-section-content h5 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 16px !important;
                line-height: 24px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${theme?.colors?.textDark || "#000000"} !important;
              }
              .about-section-content h6 {
                font-family: ${theme?.fonts?.heading || "Playfair Display, serif"} !important;
                font-weight: ${theme?.fonts?.headingWeight || "700"} !important;
                font-size: 14px !important;
                line-height: 20px !important;
                margin-bottom: 0.5rem !important;
                margin-top: 1rem !important;
                color: ${theme?.colors?.textDark || "#000000"} !important;
              }
            `}</style>
            <div
              className="about-section-content prose prose-lg w-full max-w-none text-[16px] leading-[22px]" // eslint-disable-line tailwindcss/no-custom-classname
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.textDark || "#000000"
              }}
              dangerouslySetInnerHTML={{ __html: section.body }}
            />
          </>
        )}
      </div>

      {/* CTA Button */}
      {showCTA && (
        <button
          onClick={handleCTAClick}
          className={twMerge(
            "inline-flex items-center justify-center px-10 py-3 text-[16px] font-normal leading-[24px] text-white transition-all hover:shadow-lg",
            getButtonClasses(theme?.buttonStyle)
          )}
          style={applyButtonTheme(
            {
              backgroundColor: theme?.colors?.primary || "#BD654F",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
            },
            theme
          )}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
          }}>
          {ctaText}
        </button>
      )}
    </div>
  )

  // Mobile-specific layouts
  switch (layout) {
    case "image_left":
    case "image_right":
      return (
        <section
          className={twMerge(
            "relative flex items-center justify-start",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto w-full px-4 py-8">
            <div className="flex w-full flex-col items-center gap-5">
              {/* Mobile Image */}
              {section.imageUrl ? (
                <div className="relative w-full">
                  <div
                    className={twMerge("h-[320px] w-full bg-cover bg-center", getThemeShapeClasses(theme))}
                    style={{
                      backgroundImage: `url(${section.imageUrl})`,
                      backgroundColor: `${themeColors.bgMedium || "#E6DAD7"} !important`
                    }}
                  />
                </div>
              ) : (
                <div className="relative w-full">
                  <div
                    className={twMerge(
                      "flex h-[320px] w-full items-center justify-center bg-gray-200",
                      getThemeShapeClasses(theme)
                    )}>
                    <span className="text-gray-500">No image selected</span>
                  </div>
                </div>
              )}
              <div className="w-full flex-1">{renderContent()}</div>
            </div>
          </AnimatedSection>
        </section>
      )

    case "centered":
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto w-full px-4 text-center">
            {section.imageUrl && (
              <div className="mb-6 flex justify-center">
                <div className="relative">
                  <div
                    className={twMerge("absolute -bottom-4 -right-4 h-16 w-12", getThemeShapeClasses(theme))}
                    style={{ backgroundColor: theme?.colors?.bgMedium || "#E6DAD7" }}
                  />
                  <img
                    src={section.imageUrl}
                    alt={section.headline || "About image"}
                    className={twMerge("relative h-48 w-48 object-cover", getThemeShapeClasses(theme))}
                  />
                </div>
              </div>
            )}
            {renderContent()}
          </AnimatedSection>
        </section>
      )

    case "no_image":
    default:
      return (
        <section
          className={twMerge(
            "relative flex min-h-[400px] items-center justify-center py-8",
            isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
          )}
          style={{ backgroundColor: resolveSectionBackgroundColor() }}>
          <AnimatedSection className="mx-auto w-full px-4">{renderContent()}</AnimatedSection>
        </section>
      )
  }
}

export default AboutSectionMobile
